import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import Scrubber from "./Scrubber";
import Events from "./Events";
import { track, center } from "./EventsTrack.module.scss";
import {
  scrubberCaller, scrubberAgent, scrubberDefault, scrubberCallerAgent, scrubberMatchedPhrase, scrubberRedactedSection
} from "./Scrubber.module.scss";

const calculateEventsByPixel = ({ events, progressPixelFloor }) => events.reduce((eventsByPixel, event) => {
  for (let i = progressPixelFloor(event.startTime); i <= progressPixelFloor(event.endTime); i++) {
    if (!eventsByPixel[i]) {
      eventsByPixel[i] = [];
    }
    eventsByPixel[i].push(event);
  }

  return eventsByPixel;
}, {});

const scrubberSpeakerClassName = ({ currentEvents }) => {
  const currentSpeakers = new Set();
  (currentEvents || []).forEach(({ speaker }) => currentSpeakers.add(speaker));

  if (currentSpeakers.has("agent") && currentSpeakers.has("caller")) {
    return scrubberCallerAgent;
  } else if (currentSpeakers.has("caller")) {
    return scrubberCaller;
  } else if (currentSpeakers.has("agent")) {
    return scrubberAgent;
  }
};

const innerEventMatchesPosition = ({ eventArrays, position }) => {
  return eventArrays.some(eventArray =>
    eventArray && eventArray.some(event => event.startTime <= position && event.endTime >= position)
  );
};

const scrubberClassNamesFromEvents = ({ currentEvents, position }) => {
  const classNames = [];

  const scrubberSpeaker = scrubberSpeakerClassName({ currentEvents, position });
  if (scrubberSpeaker) {
    classNames.push(scrubberSpeaker);
  }

  if (currentEvents && innerEventMatchesPosition({ eventArrays: currentEvents.map(event => event.redactedSections), position })) {
    classNames.push(scrubberRedactedSection);
  }

  if (currentEvents && innerEventMatchesPosition({ eventArrays: currentEvents.map(event => event.matchedPhrases), position })) {
    classNames.push(scrubberMatchedPhrase);
  }

  return classNames.length > 0 ? classNames : [scrubberDefault];
};

const EventsTrack = ({ events, position, onSeek, progressPercent, progressPixel }) => {
  const progressPixelFloor = useCallback((currentPosition) => Math.floor(progressPixel(currentPosition)), [progressPixel]);
  const eventsByPixel = useMemo(() => calculateEventsByPixel({ events, progressPixelFloor }), [events, progressPixel]);

  const positionPixel = progressPixelFloor(position);
  const currentEvents = eventsByPixel[positionPixel];
  return (
    <div className={track}>
      <div className={center} />
      <Events events={events} progressPercent={progressPercent} position={position}/>
      <Scrubber className={classNames(scrubberClassNamesFromEvents({ currentEvents, position }))} left={positionPixel} onMouseDown={onSeek} />
    </div>
  );
};

EventsTrack.propTypes = {
  events: PropTypes.array.isRequired,
  position: PropTypes.number.isRequired,
  progressPercent: PropTypes.func.isRequired,
  progressPixel: PropTypes.func.isRequired,
  onSeek: PropTypes.func
};

EventsTrack.defaultProps = {
  onSeek: () => {}
};

export default EventsTrack;
